import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  type Notification,
  type NotificationStatusFilter,
  type NotificationTypeFilter,
  notificationTarget,
} from '@/api/adapters/notifications';
import { toast } from '@/app/store/toast.store';
import { Select } from '@/components/ui/Select';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationViewed,
  useNotifications,
} from '@/shared/hooks/use-notifications';

import { NotificationItem } from './NotificationItem';

// PRD 08 v1.3 — 공용 수신구: 환자 이벤트(고위험·PRO 미응답) + 운영 공지를 한 화면에서 조회·확인.
export function MedicalNotificationsPage() {
  const { t } = useTranslation('medical');
  const navigate = useNavigate();
  const [type, setType] = useState<NotificationTypeFilter>('ALL');
  const [status, setStatus] = useState<NotificationStatusFilter>('ALL');
  // Only one SYSTEM card may be expanded at a time (PRD §3.2, expand/collapse).
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { notifications, unreadCount, isLoading, isError, refetch } =
    useNotifications(type, status);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const markViewed = useMarkNotificationViewed();

  // PRD §2.2 #2 — 전체 유형 / 환자 이벤트 / 공지. Changing type preserves status and vice-versa.
  const typeOptions = [
    { value: 'ALL', label: t('notifications.typeFilter.all') },
    { value: 'EVENT', label: t('notifications.typeFilter.event') },
    { value: 'SYSTEM', label: t('notifications.typeFilter.system') },
  ];
  const statusOptions = [
    { value: 'ALL', label: t('notifications.filter.all') },
    { value: 'UNREAD', label: t('notifications.filter.unread') },
    { value: 'READ', label: t('notifications.filter.read') },
  ];

  // PRD §3.2 — activating a card: mark unread→read, then navigate (환자 이벤트) or toggle
  // in-place expand (긴 SYSTEM 공지). Short read notices are inert.
  const handleActivate = (n: Notification) => {
    if (n.status === 'UNREAD') {
      markRead.mutate(n.id, {
        onError: () => toast.error(t('notifications.toast.markReadFail')),
      });
    }
    const target = notificationTarget(n);
    if (target) {
      navigate(target);
      return;
    }
    // SHCT-652 — 조회수. 어드민 PRD 13 §3.1 counts a 의료진웹 open of the notice, and PRD 08 v1.1 §3.2
    // makes that open the in-place body expand. Counted on expand only (never on collapse) and on
    // EVERY expand, not just the first — the PRD says "진입할 때마다", so `markRead` (once-per-staff)
    // deliberately does not carry this. Errors are swallowed: the counter is admin-side telemetry
    // this screen never shows, so a failure must not surface a toast over the notice body.
    if (n.type === 'SYSTEM' && expandedId !== n.id) {
      markViewed.mutate(n.id, { onError: () => undefined });
    }
    // SYSTEM: in-place expand/collapse (single-open).
    setExpandedId((cur) => (cur === n.id ? null : n.id));
  };

  // PRD §3.3 — bulk confirm; no-op (skip API) when nothing is unread.
  const handleMarkAll = () => {
    if (unreadCount === 0 || markAllRead.isPending) return;
    markAllRead.mutate(undefined, {
      onSuccess: () => toast.success(t('notifications.toast.markAllSuccess')),
      onError: () => toast.error(t('notifications.toast.markReadFail')),
    });
  };

  return (
    <div>
      <div className="content__header">
        <h1 className="content__title">{t('notifications.title')}</h1>
        <div className="content__subtitle">{t('notifications.subtitle')}</div>
      </div>

      <div className="content__body">
        <div className="filter-bar">
          <Select
            value={type}
            onChange={(value) => setType(value as NotificationTypeFilter)}
            options={typeOptions}
          />
          <Select
            value={status}
            onChange={(value) => setStatus(value as NotificationStatusFilter)}
            options={statusOptions}
          />
          <div style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={handleMarkAll}
              disabled={unreadCount === 0 || markAllRead.isPending}
            >
              {t('notifications.markAllRead')}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card__body card__body--no-padding">
            {isLoading ? (
              <ul className="notif-list" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="notif-item notif-item--skeleton">
                    <div className="notif-item__body">
                      <div className="notif-skeleton notif-skeleton--title" />
                      <div className="notif-skeleton notif-skeleton--desc" />
                      <div className="notif-skeleton notif-skeleton--meta" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : isError ? (
              <div className="empty-state">
                <p>{t('notifications.error.load')}</p>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => refetch()}
                >
                  {t('notifications.error.retry')}
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="empty-state">
                <p>{t('notifications.empty')}</p>
              </div>
            ) : (
              <ul className="notif-list">
                {notifications.map((n) => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    isExpanded={expandedId === n.id}
                    onActivate={() => handleActivate(n)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MedicalNotificationsPage;
